import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { useEntityStore, usePortfolioPrefsStore } from '../store';
import { subscriptionsAPI } from '../lib/api';
import { getErrorMessage } from '../lib/errors';
import { cn } from '../lib/utils';
import { SPEND_TYPE_TABS, SPEND_TYPE_BADGE, reasonText } from './SubscriptionsByJurisdictionTab';

// One flat, searchable list of the SAME rows the stat cards and the grouped
// tab add up (GET /subscriptions/by-jurisdiction, flattened). It replaces the
// old "Detected" table, which read a separate legacy list and so never matched
// the cards: duplicates, card lines, stale renewal dates (Fan, 2026-10-29).

const SYMBOLS = { GBP: '\u00A3', EUR: '\u20AC', USD: '$', DKK: 'kr ' };

function money(value, currency) {
  const n = Number(value || 0);
  return `${SYMBOLS[currency] || `${currency} `}${n.toLocaleString('en-GB', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
}

export function flattenVendorRows(data) {
  const rows = [];
  (data?.jurisdictions || []).forEach((j) => {
    (j.entities || []).forEach((e) => {
      (e.vendors || []).forEach((v) => {
        rows.push({ ...v, entity_id: e.entity_id, entity_name: e.entity_name, jurisdiction: j.jurisdiction_label });
      });
    });
  });
  rows.sort((a, b) => Number(b.monthly || 0) - Number(a.monthly || 0));
  return rows;
}

export function SubscriptionsVendorListTab({ scope, fixedSpendType }) {
  const selectedEntity = useEntityStore((s) => s.selectedEntity);
  const displayCurrency = usePortfolioPrefsStore((s) => s.displayCurrency);
  const [pickedType, setSpendType] = useState('all');
  // A fixed type (Subscriptions / Other fixed costs tabs) hides the type pills.
  const spendType = fixedSpendType || pickedType;
  const [query, setQuery] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const entityParam = selectedEntity && selectedEntity !== 'all' && !String(selectedEntity).startsWith('group:')
    ? selectedEntity
    : undefined;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await subscriptionsAPI.byJurisdiction({
        entityId: entityParam,
        scope: scope === 'personal' ? 'personal' : 'entities',
        displayCurrency,
        spendType: spendType === 'all' ? undefined : spendType,
      });
      setData(res.data);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [entityParam, scope, displayCurrency, spendType]);

  useEffect(() => { load(); }, [load]);

  const cur = data?.display_currency || displayCurrency;
  const rows = useMemo(() => {
    const all = flattenVendorRows(data);
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter((r) => `${r.vendor} ${r.entity_name}`.toLowerCase().includes(q));
  }, [data, query]);
  const total = rows.reduce((acc, r) => acc + Number(r.unconvertible ? 0 : r.monthly || 0), 0);

  return (
    <div className="space-y-4" data-testid="subs-vendor-list">
      <div className="flex flex-wrap items-center gap-2">
        {!fixedSpendType && (<div className="inline-flex h-8 items-center rounded-lg bg-muted p-0.5">
          {SPEND_TYPE_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setSpendType(t.key)}
              className={cn(
                'px-2.5 py-1 text-xs font-medium rounded-md transition-all',
                spendType === t.key ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              )}
              data-testid={`subs-list-type-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>)}
        <div className="relative ml-auto w-64">
          <Search className="absolute left-2 top-2 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search vendor or company"
            className="h-8 pl-8 text-sm"
            data-testid="subs-list-search"
          />
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-muted-foreground py-8">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading...
        </div>
      )}
      {!loading && error && <p className="text-sm text-destructive py-4">{error}</p>}
      {!loading && !error && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground text-xs">
                  <th className="text-left py-2 px-3">Vendor</th>
                  <th className="text-left py-2 px-3">{scope === 'personal' ? 'Person' : 'Company'}</th>
                  <th className="text-left py-2 px-3">Type</th>
                  <th className="text-right py-2 px-3">Monthly</th>
                  <th className="text-right py-2 px-3">Payments (3 mo)</th>
                  <th className="text-right py-2 px-3">Last payment</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((v) => (
                  <tr
                    key={`${v.entity_id}-${v.vendor}-${v.currency}`}
                    className="border-b border-border/50"
                    data-testid={`subs-list-row-${v.entity_id || 'unassigned'}-${v.vendor}`}
                  >
                    <td className="py-1.5 px-3">
                      {v.vendor}
                      {v.sample_references?.length ? (
                        <span className="block text-[11px] text-muted-foreground truncate max-w-[280px]" title={v.sample_references.join(' | ')}>
                          {v.sample_references[0]}
                        </span>
                      ) : null}
                    </td>
                    <td className="py-1.5 px-3 text-muted-foreground">{v.entity_name}</td>
                    <td className="py-1.5 px-3">
                      <Badge
                        variant="outline"
                        className={cn('text-[10px] px-1.5 py-0', (SPEND_TYPE_BADGE[v.spend_type] || {}).className)}
                        title={reasonText(v)}
                      >
                        {(SPEND_TYPE_BADGE[v.spend_type] || {}).label || v.spend_type}
                      </Badge>
                      {v.spend_type === 'excluded' && (
                        <span className="block text-[11px] text-muted-foreground">{v.spend_type_label}</span>
                      )}
                    </td>
                    <td className="py-1.5 px-3 text-right font-mono">
                      {v.unconvertible ? money(v.monthly_native, v.currency) : money(v.monthly, cur)}
                      {v.currency !== cur && !v.unconvertible ? (
                        <span className="block text-[11px] text-muted-foreground">{money(v.monthly_native, v.currency)}</span>
                      ) : null}
                    </td>
                    <td className="py-1.5 px-3 text-right font-mono text-muted-foreground">{v.payments ?? '--'}</td>
                    <td className="py-1.5 px-3 text-right text-muted-foreground">{v.last_payment || '--'}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">Nothing in this view.</td></tr>
                )}
              </tbody>
              {rows.length > 0 && (
                <tfoot>
                  <tr className="text-xs">
                    <td className="py-2 px-3 font-medium" colSpan={3}>{rows.length} payees</td>
                    <td className="py-2 px-3 text-right font-mono font-medium">{money(total, cur)}/mo</td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              )}
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
